/**
 * 版本管理工具
 * 用于管理应用版本和token版本，实现自动退出登录和页面刷新
 */

// 版本配置
export const VERSION_CONFIG = {
  // 当前token版本，修改JWT密钥时需要更新
  TOKEN_VERSION: '1.0.0',

  // 当前应用版本，前端更新时需要更新
  APP_VERSION: '1.0.0',

  // 版本检查间隔（毫秒）
  CHECK_INTERVAL: 60000, // 60秒检查一次

  // 是否启用自动版本检查
  AUTO_CHECK: true
}

// 本地存储键名
const STORAGE_KEYS = {
  TOKEN_VERSION: 'token_version',
  APP_VERSION: 'app_version',
  LAST_CHECK_TIME: 'last_version_check_time'
}

/**
 * 获取本地存储的版本信息
 */
export const getLocalVersions = () => {
  return {
    tokenVersion: localStorage.getItem(STORAGE_KEYS.TOKEN_VERSION) || '1.0.0',
    appVersion: localStorage.getItem(STORAGE_KEYS.APP_VERSION) || '1.0.0',
    lastCheckTime: localStorage.getItem(STORAGE_KEYS.LAST_CHECK_TIME) || '0'
  }
}

/**
 * 保存版本信息到本地存储
 */
export const saveLocalVersions = (versions) => {
  if (versions.tokenVersion) {
    localStorage.setItem(STORAGE_KEYS.TOKEN_VERSION, versions.tokenVersion)
  }
  if (versions.appVersion) {
    localStorage.setItem(STORAGE_KEYS.APP_VERSION, versions.appVersion)
  }
  localStorage.setItem(STORAGE_KEYS.LAST_CHECK_TIME, Date.now().toString())
}

/**
 * 清除版本信息
 */
export const clearLocalVersions = () => {
  localStorage.removeItem(STORAGE_KEYS.TOKEN_VERSION)
  localStorage.removeItem(STORAGE_KEYS.APP_VERSION)
  localStorage.removeItem(STORAGE_KEYS.LAST_CHECK_TIME)
}

/**
 * 比较版本号
 * @param {string} version1 版本1
 * @param {string} version2 版本2
 * @returns {number} 1: version1 > version2, -1: version1 < version2, 0: 相等
 */
export const compareVersions = (version1, version2) => {
  const v1Parts = version1.split('.').map(Number)
  const v2Parts = version2.split('.').map(Number)

  const maxLength = Math.max(v1Parts.length, v2Parts.length)

  for (let i = 0; i < maxLength; i++) {
    const v1 = v1Parts[i] || 0
    const v2 = v2Parts[i] || 0

    if (v1 > v2) return 1
    if (v1 < v2) return -1
  }

  return 0
}

/**
 * 检查是否需要更新
 */
export const checkForUpdates = () => {
  const localVersions = getLocalVersions()
  const currentTime = Date.now()
  const lastCheckTime = parseInt(localVersions.lastCheckTime)

  // 检查是否到了检查时间
  if (currentTime - lastCheckTime < VERSION_CONFIG.CHECK_INTERVAL) {
    return { needsCheck: false }
  }

  // 检查token版本
  const tokenVersionMatch = localVersions.tokenVersion === VERSION_CONFIG.TOKEN_VERSION
  if (!tokenVersionMatch) {
    return {
      needsCheck: true,
      needsLogout: true,
      reason: 'token_version_mismatch',
      current: localVersions.tokenVersion,
      expected: VERSION_CONFIG.TOKEN_VERSION
    }
  }

  // 检查应用版本
  const appVersionMatch = localVersions.appVersion === VERSION_CONFIG.APP_VERSION
  if (!appVersionMatch) {
    return {
      needsCheck: true,
      needsRefresh: true,
      reason: 'app_version_mismatch',
      current: localVersions.appVersion,
      expected: VERSION_CONFIG.APP_VERSION
    }
  }

  // 更新检查时间
  saveLocalVersions({})

  return { needsCheck: true, upToDate: true }
}

/**
 * 版本检查器类
 */
export class VersionChecker {
  constructor() {
    this.checkInterval = null
    this.isChecking = false
  }

  /**
   * 开始自动版本检查
   */
  startAutoCheck() {
    if (!VERSION_CONFIG.AUTO_CHECK || this.checkInterval) {
      return
    }

    this.checkInterval = setInterval(() => {
      this.performCheck()
    }, VERSION_CONFIG.CHECK_INTERVAL)

    console.log('版本自动检查已启动，检查间隔:', VERSION_CONFIG.CHECK_INTERVAL, 'ms')
  }

  /**
   * 停止自动版本检查
   */
  stopAutoCheck() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval)
      this.checkInterval = null
      console.log('版本自动检查已停止')
    }
  }

  /**
   * 执行版本检查
   */
  async performCheck() {
    if (this.isChecking) {
      return
    }

    this.isChecking = true

    try {
      const result = checkForUpdates()

      if (result.needsCheck && !result.upToDate) {
        // 触发版本更新事件
        this.handleVersionUpdate(result)
      }
    } catch (error) {
      console.error('版本检查失败:', error)
    } finally {
      this.isChecking = false
    }
  }

  /**
   * 处理版本更新
   */
  handleVersionUpdate(result) {
    if (result.needsLogout) {
      console.warn('Token版本不匹配，需要重新登录:', result)

      // 触发退出登录事件
      const event = new CustomEvent('version:logout', {
        detail: {
          reason: result.reason,
          current: result.current,
          expected: result.expected
        }
      })
      window.dispatchEvent(event)
    }

    if (result.needsRefresh) {
      console.log('应用版本已更新，需要刷新页面:', result)

      // 触发页面刷新事件
      const event = new CustomEvent('version:refresh', {
        detail: {
          reason: result.reason,
          current: result.current,
          expected: result.expected
        }
      })
      window.dispatchEvent(event)
    }
  }

  /**
   * 手动触发版本检查
   */
  async manualCheck() {
    await this.performCheck()
  }
}

// 创建全局版本检查器实例
export const versionChecker = new VersionChecker()

// 导出默认配置
export default {
  VERSION_CONFIG,
  getLocalVersions,
  saveLocalVersions,
  clearLocalVersions,
  compareVersions,
  checkForUpdates,
  versionChecker
}
